import {
  type Question,
  type Section,
} from "@/components/DirectorySubmissions/Form/types";
import { useIsFeatureEnabled } from "@precedent/shared-util";

type MatterType = "publishable" | "confidential";

export const createMatterQuestion = (
  type: MatterType,
  index: number,
): Question => ({
  id: `${type === "publishable" ? "PM" : "CM"}${index}`,
  highlight: true,
  shortName: `${type.charAt(0).toUpperCase() + type.slice(1)} ${index}`,
  question: `${type.charAt(0).toUpperCase() + type.slice(1)} matter ${index}`,
  type: "highlight",
  fields: [
    {
      name:
        type === "publishable"
          ? "Name of client – this will be publishable. If you cannot reveal the client name, give a general description."
          : "Name of client (for ranking purposes only)",
      shortName: "Client Name",
      type: "text",
    },
    {
      name: "Summary of matter and your department's role – Please say why this matter was important. Also, tell us exactly what role your department played.",
      shortName: "Matter Summary",
      type: "textarea",
    },
    {
      name: "Matter value – include currency and amount in figures",
      shortName: "Matter Value",
      type: "text",
    },
    {
      name: "Is this a cross-border matter? If yes, please indicate the jurisdictions involved.",
      shortName: "Cross Border",
      type: "text",
    },
    {
      name: "Lead partner",
      shortName: "Lead Partner",
      type: "text",
    },
    {
      name: "Other team members",
      shortName: "Other Team Members",
      type: "text",
      required: false,
    },
    {
      name: "Other firms advising on the matter and their role(s)",
      shortName: "Other Firms",
      type: "text",
      required: false,
    },
    {
      name: "Date of completion or current status",
      shortName: "Completion Date",
      type: "text",
    },
    {
      name: "Other information about this matter – e.g. link to press coverage",
      shortName: "Other Info",
      type: "textarea",
      required: false,
    },
    {
      name: "Tags",
      shortName: "Tags",
      type: "tags",
      required: false,
      shouldRender: () => useIsFeatureEnabled("tags") || false,
    },
  ],
});

export const createMatterQuestions = (
  type: MatterType,
  count: number,
  includeClients: boolean = true,
): Question[] => {
  const initialQuestion: Question = {
    id: type === "publishable" ? "PC" : "CC",
    shortName: `${type.charAt(0).toUpperCase() + type.slice(1)} Clients`,
    question: `${type.toUpperCase()} CLIENTS – List of this department's ${type.toUpperCase()} clients. Please indicate whether a client is a new client (within the last 12 months).${type === "confidential" ? " If this information is not known, leave the field blank." : ""}`,
    type: "table",
    fields: [
      { name: "Name of Client", type: "text" },
      { name: "New Client", type: "bool" },
    ],
  };

  return includeClients
    ? [
        initialQuestion,
        ...Array.from({ length: count }, (_, i) =>
          createMatterQuestion(type, i + 1),
        ),
      ]
    : Array.from({ length: count }, (_, i) =>
        createMatterQuestion(type, i + 1),
      );
};

const generateSections = (
  publicMatterCount: number,
  confidentialMatterCount: number,
): Section[] => [
  {
    title: "Overview information",
    color: "#ffffcc",
    questions: [
      {
        id: "A1",
        shortName: "Firm Name",
        question: "Firm name",
        type: "text",
      },
      {
        id: "A2",
        shortName: "Practice Area",
        question: "Practice Area",
        type: "text",
        readOnly: true,
      },
      {
        id: "A3",
        shortName: "Location",
        question: "Location (Jurisdiction)",
        type: "text",
        readOnly: true,
      },
      {
        id: "A4",
        shortName: "Contact Persons",
        question:
          "Contact person(s) to arrange interviews about this practice area",
        type: "table",
        fields: [
          { name: "Name", type: "text" },
          { name: "Email", type: "text" },
          { name: "Telephone number", type: "text" },
        ],
      },
      {
        id: "B1",
        shortName: "Department Name",
        question: "Department name (used by firm)",
        type: "text",
      },
      {
        id: "B2",
        shortName: "Partners Count",
        question: "Partners Count",
        type: "text",
      },
      {
        id: "B3",
        shortName: "Other Lawyers Count",
        question: "Other Lawyers Count",
        type: "text",
      },
      {
        id: "B4",
        shortName: "Dept Heads/Key Partners",
        question: "Department Head(s) or Key Partners",
        type: "table",
        fields: [
          { name: "Name", type: "text" },
          { name: "Email", type: "text" },
          { name: "Telephone number", type: "text" },
        ],
      },
      {
        id: "B5",
        shortName: "Partner Changes",
        question: "Hires / Departures of partners in last 12 months",
        type: "table",
        fields: [
          { name: "Name", type: "text" },
          { name: "Joined / Departed", type: "text" },
          { name: "Joined From / Destination (firm)", type: "text" },
        ],
      },
      {
        id: "B6",
        shortName: "Lawyer Info",
        question:
          "Information regarding Ranked and Unranked lawyers (including associates) in this practice area",
        type: "table",
        fields: [
          { name: "Name", type: "text" },
          { name: "Comments or Web Link", type: "textarea" },
          { name: "Partner", type: "bool" },
          { name: "Ranked", type: "bool" },
          {
            name: "Current/recent parental leave, significant childcare commitments or other part-time working arrangements, if applicable",
            type: "text",
            required: false,
          },
        ],
      },
      {
        id: "B7",
        shortName: "Dept Specialties",
        question: "What is this department best known for?",
        type: "textarea",
      },
      {
        id: "C1",
        shortName: "Barristers Used",
        question:
          "If you have used barristers / advocates in the UK, Australia, Hong Kong, India, Malaysia, New Zealand or Sri Lanka please provide the information below (Optional)",
        type: "table",
        fields: [
          { name: "Barrister/advocate name", type: "text" },
          { name: "Firm / Set", type: "text" },
          { name: "Comments", type: "textarea" },
        ],
        required: false,
      },
      {
        id: "C2",
        shortName: "Coverage Feedback",
        question: "Feedback on our coverage of this practice area (Optional)",
        type: "textarea",
        required: false,
      },
    ],
  },
  {
    title: "Public clients",
    color: "#cbffcc",
    questions: createMatterQuestions("publishable", publicMatterCount),
  },
  {
    title: "Confidential clients",
    color: "#ffcccc",
    questions: createMatterQuestions("confidential", confidentialMatterCount),
  },
];

export default generateSections;
